import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY } from "./nyHelpers";

// Template90: Día 7 Día libre (Page21). Contenido desde page.
const Template90 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		if (!text) return null;
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const title = page?.texts?.[0]?.content;
	const heroH1 = page?.texts?.[1]?.content;
	const intro = page?.texts?.[2]?.content;
	const brooklynH3 = page?.texts?.[3]?.content;
	const brooklynP = page?.texts?.[4]?.content;
	const sabiasQueBrooklyn = parseSabiasQueItem(page?.texts?.[5]?.content);
	const tribecaH3 = page?.texts?.[6]?.content;
	const tribecaP = page?.texts?.[7]?.content;
	const sabiasQueTribeca = parseSabiasQueItem(page?.texts?.[8]?.content);
	const cazafantasmasRaw = page?.texts?.[9]?.content;
	const cazafantasmasTitle = cazafantasmasRaw?.includes("\n")
		? cazafantasmasRaw.split("\n")[0]
		: cazafantasmasRaw;
	const cazafantasmasBody = cazafantasmasRaw?.includes("\n")
		? cazafantasmasRaw.split("\n").slice(1).join(" ").trim()
		: "";
	const comprasText = page?.texts?.[10]?.content;
	const museosH4 = page?.texts?.[11]?.content;
	const museosP = page?.texts?.[12]?.content;
	const nbaHtml = page?.texts?.[13]?.content;

	const heroSrc = getImageSrc("hero");
	const diaLibreSrc = getImageSrc("dia-libre");
	const williamsburgSrc = getImageSrc("williamsburg");
	const tribecaSrc = getImageSrc("tribeca");
	const cazafantasmasSrc = getImageSrc("cazafantasmas");
	const nbaStickerSrc = getImageSrc("nba-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	const estiloH3 = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#caa100",
					textDecoration: "underline",
					fontWeight: "bold",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			{heroSrc && (
				<div data-block-id="image-0" style={{ position: "relative" }}>
					<img
						src={heroSrc}
						alt="Día libre"
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{heroH1 && (
						<h1
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: 32,
								position: "absolute",
								bottom: 0,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
								margin: 10,
							}}
						>
							{heroH1}
						</h1>
					)}
					{diaLibreSrc && (
						<img
							src={diaLibreSrc}
							alt="NY"
							style={{ width: 90, position: "absolute", bottom: 55, left: 20 }}
						/>
					)}
				</div>
			)}

			{intro && (
				<div data-block-id="text-1" style={{ padding: "10px 12px" }}>
					<p style={{ ...estiloP, color: "#333" }}>{intro}</p>
				</div>
			)}

			<div
				style={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					paddingTop: 0,
					gap: 10,
				}}
			>
				{(brooklynH3 || brooklynP || williamsburgSrc) && (
					<div data-block-id="text-2" style={{ display: "flex", gap: 16, position: "relative" }}>
						{williamsburgSrc && (
							<img
								src={williamsburgSrc}
								alt="Williamsburg"
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<div style={{ width: "60%" }}>
							{brooklynH3 && <h3 style={estiloH3}>{brooklynH3}</h3>}
							{brooklynP && <p style={estiloP}>{brooklynP}</p>}
						</div>
						{sabiasQueBrooklyn && (
							<div style={{ position: "absolute", top: 0, left: "33%" }}>
								<SabiasQueNY
									icono={sabiasQueBrooklyn.icono}
									texto={sabiasQueBrooklyn.texto}
								/>
							</div>
						)}
					</div>
				)}

				{(tribecaH3 || tribecaP || tribecaSrc) && (
					<div data-block-id="text-3" style={{ display: "flex", gap: 16, position: "relative" }}>
						{tribecaSrc && (
							<img
								src={tribecaSrc}
								alt="Tribeca"
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<div style={{ width: "60%" }}>
							{tribecaH3 && <h3 style={estiloH3}>{tribecaH3}</h3>}
							{tribecaP && <p style={estiloP}>{tribecaP}</p>}
						</div>
						{sabiasQueTribeca && (
							<div
								style={{
									position: "absolute",
									top: isSmallScreen ? -10 : -30,
									right: "26%",
								}}
							>
								<SabiasQueNY
									icono={sabiasQueTribeca.icono}
									texto={sabiasQueTribeca.texto}
								/>
							</div>
						)}
						{cazafantasmasSrc && (cazafantasmasTitle || cazafantasmasBody) && (
							<>
								<img
									src={cazafantasmasSrc}
									alt="Cazafantasmas"
									style={{
										width: 110,
										position: "absolute",
										bottom: isSmallScreen ? -160 : -30,
										left: -30,
									}}
								/>
								<div
									style={{
										position: "absolute",
										bottom: isSmallScreen ? -80 : 50,
										left: 0,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: '"Poppins", sans-serif',
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 120,
									}}
								>
									{cazafantasmasTitle && <strong>{cazafantasmasTitle}</strong>}
									{cazafantasmasBody && (
										<>
											<br />
											{cazafantasmasBody}
										</>
									)}
								</div>
							</>
						)}
					</div>
				)}

				{comprasText && (
					<div
						data-block-id="text-4"
						style={{
							display: "flex",
							justifyContent: isSmallScreen ? "flex-end" : "flex-start",
						}}
					>
						<div
							style={{
								backgroundColor: "#e0f7fa",
								padding: 12,
								borderRadius: 12,
								display: "flex",
								gap: 16,
								alignItems: "center",
								width: isSmallScreen ? "60%" : "100%",
							}}
						>
							<p style={estiloP}>{comprasText}</p>
						</div>
					</div>
				)}

				{(museosH4 || museosP) && (
					<div data-block-id="text-5" style={{ position: "relative" }}>
						<div
							style={{
								backgroundColor: "#e0f7fa",
								border: "3px solid #59c3d8",
								borderRadius: 12,
								padding: "12px 16px",
								fontSize: 12,
								fontFamily: '"Poppins", sans-serif',
								color: "#333",
								width: "60%",
							}}
						>
							{museosH4 && (
								<h4
									style={{
										margin: 0,
										marginBottom: 8,
										fontSize: 13,
										fontWeight: 700,
										color: "#007a8d",
										textTransform: "uppercase",
									}}
								>
									{museosH4}
								</h4>
							)}
							{museosP && <p style={estiloP}>{museosP}</p>}
						</div>
						{nbaStickerSrc && nbaHtml && (
							<>
								<img
									src={nbaStickerSrc}
									alt="NBA"
									style={{
										width: 110,
										position: "absolute",
										bottom: isSmallScreen ? -50 : 0,
										right: -30,
									}}
								/>
								<div
									style={{
										position: "absolute",
										bottom: isSmallScreen ? 30 : 40,
										right: isSmallScreen ? 0 : 50,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: '"Poppins", sans-serif',
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 140 : 200,
									}}
								>
									<span dangerouslySetInnerHTML={{ __html: nbaHtml }} />
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</Box>
	);
};

export default Template90;
